import { useEffect, useState, useRef } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, Panel, Pill, EmptyState, StatCard, Avatar } from "@/components/Bits";
import { toast } from "sonner";
import {
  CheckCircle, Clock, Warning, Camera, MapPin, ChalkboardTeacher,
  ChatTeardropText, Lifebuoy, Sparkle, BookOpen, X,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";

const STATUS_TONE = {
  open: "mustard", in_progress: "lavender", done: "sage",
  skipped: "ink", needs_help: "danger",
};

export default function TeacherTasks() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [completing, setCompleting] = useState(null);

  const load = async () => {
    const r = await api.get("/hrms/tasks");
    setTasks(r.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = tasks.filter(t => filter === "all" || t.status === filter);
  const stats = {
    total: tasks.length,
    open: tasks.filter(t => t.status === "open").length,
    inProgress: tasks.filter(t => t.status === "in_progress").length,
    done: tasks.filter(t => t.status === "done").length,
    needsHelp: tasks.filter(t => t.status === "needs_help").length,
  };

  const setStatus = async (taskId, status) => {
    await api.patch(`/hrms/tasks/${taskId}/status?status=${status}`);
    toast.success(`Marked as ${status.replace("_", " ")}`);
    load();
  };

  return (
    <div data-testid="teacher-tasks-page">
      <PageHeader
        eyebrow="Curriculum"
        title="My Implementation Tasks"
        subtitle="Auto-assigned from this branch's curriculum plans. Mark progress, upload evidence, request admin help."
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total" value={stats.total} icon={BookOpen} accent="terracotta" />
        <StatCard label="Open" value={stats.open} icon={Clock} accent="mustard" />
        <StatCard label="In Progress" value={stats.inProgress} icon={Sparkle} accent="lavender" />
        <StatCard label="Done" value={stats.done} icon={CheckCircle} accent="sage" />
        <StatCard label="Need Help" value={stats.needsHelp} icon={Lifebuoy} accent="danger" />
      </div>

      <div className="flex flex-wrap gap-2 mb-5 overflow-x-auto">
        {["all", "open", "in_progress", "done", "needs_help", "skipped"].map(f => (
          <button key={f} data-testid={`task-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize whitespace-nowrap ${filter === f ? "bg-ink text-white" : "bg-white border border-border"}`}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState title="No tasks here" hint="Try a different filter." /> : (
        <div className="space-y-3" data-testid="tasks-list">
          {filtered.map(t => (
            <motion.div key={t.task_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-border p-4" data-testid={`task-${t.task_id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-xl shrink-0 flex items-center justify-center font-bold text-sm ${t.status === "done" ? "bg-sage-soft text-sage-deep" : "bg-mustard-soft text-mustard-hover"}`}>
                  {t.week ? `W${t.week}` : "·"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold">{t.title}</span>
                    <Pill tone={STATUS_TONE[t.status] || "ink"}>{t.status.replace("_", " ")}</Pill>
                    {t.admin_review === "approved" && <Pill tone="sage">✓ Approved</Pill>}
                    {t.admin_review === "needs_revision" && <Pill tone="danger">Revise</Pill>}
                  </div>
                  {t.learning_objective && (
                    <div className="text-xs text-ink-soft mb-1">🎯 {t.learning_objective}</div>
                  )}
                  <div className="text-sm">{t.description}</div>
                  {(t.materials || []).length > 0 && (
                    <div className="mt-2 text-xs"><b>Materials:</b> {t.materials.join(", ")}</div>
                  )}
                  {(t.checklist || []).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-terracotta font-semibold cursor-pointer">Checklist ({t.checklist.length})</summary>
                      <ul className="mt-1 ml-5 text-xs text-ink-soft list-disc">
                        {t.checklist.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </details>
                  )}
                  {t.remarks && (
                    <div className="mt-2 text-xs italic text-ink-soft">📝 "{t.remarks}"</div>
                  )}
                  {t.review_note && (
                    <div className="mt-2 text-xs text-ink bg-mustard-soft px-2 py-1 rounded-xl"><b>Admin:</b> {t.review_note}</div>
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 pl-15">
                {t.status !== "done" && (
                  <>
                    {t.status === "open" && (
                      <button data-testid={`start-${t.task_id}`} onClick={() => setStatus(t.task_id, "in_progress")} className="px-3 py-1.5 rounded-full bg-lavender-soft text-lavender-hover text-xs font-semibold">Start</button>
                    )}
                    <button data-testid={`complete-${t.task_id}`} onClick={() => setCompleting(t)} className="px-3 py-1.5 rounded-full bg-sage text-white text-xs font-semibold flex items-center gap-1">
                      <CheckCircle weight="duotone" size={12} /> Complete with evidence
                    </button>
                    <button data-testid={`help-${t.task_id}`} onClick={() => setStatus(t.task_id, "needs_help")} className="px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold flex items-center gap-1">
                      <Lifebuoy weight="duotone" size={12} /> Need help
                    </button>
                    <button data-testid={`skip-${t.task_id}`} onClick={() => setStatus(t.task_id, "skipped")} className="px-3 py-1.5 rounded-full bg-white border border-border text-xs">
                      Skip
                    </button>
                  </>
                )}
                {t.status === "done" && t.admin_review === "needs_revision" && (
                  <button data-testid={`redo-${t.task_id}`} onClick={() => setStatus(t.task_id, "in_progress")} className="px-3 py-1.5 rounded-full bg-mustard text-white text-xs font-semibold">Redo</button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {completing && <CompleteDialog task={completing} onClose={() => setCompleting(null)} onSaved={() => { setCompleting(null); load(); }} />}
    </div>
  );
}

function CompleteDialog({ task, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [evidenceFileIds, setEvidenceFileIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload?folder=evidence", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEvidenceFileIds(prev => [...prev, data.file_id]);
      toast.success("Photo added");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      let geo = null;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 }));
        geo = { lat: pos.coords.latitude, lon: pos.coords.longitude };
      } catch {}
      await api.post(`/hrms/tasks/${task.task_id}/complete`, {
        completed_items: (task.checklist || []).map((_, i) => i),
        note,
        evidence_file_ids: evidenceFileIds,
        geo,
      });
      toast.success("Submitted to admin for review 🎉");
      onSaved();
    } catch {
      toast.error("Could not submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="complete-dialog">
        <div className="font-heading font-bold text-xl">Complete with evidence</div>
        <div className="text-xs text-ink-soft mt-1">{task.title}</div>
        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onPickFile} className="hidden" data-testid="evidence-input" />
        <div className="mt-4 space-y-3">
          <div>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-evidence" className="w-full h-11 rounded-2xl bg-paper-alt border border-dashed border-border flex items-center justify-center gap-2 hover:bg-mustard-soft text-sm">
              <Camera weight="duotone" /> {uploading ? "Uploading…" : "Add photo / video evidence"}
            </button>
            {evidenceFileIds.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {evidenceFileIds.map(id => (
                  <div key={id} className="w-16 h-16 rounded-xl overflow-hidden border border-border relative">
                    <img src={`${API_BASE}/files/${id}`} alt="evidence" className="w-full h-full object-cover" />
                    <button onClick={() => setEvidenceFileIds(prev => prev.filter(f => f !== id))} className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center"><X size={10} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-soft">Teacher remarks (parent-friendly)</label>
            <textarea data-testid="complete-note" value={note} onChange={e => setNote(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-xl bg-paper-alt border border-border text-sm" placeholder="A warm note about how the class went..." />
          </div>
          <div className="text-xs text-ink-soft flex items-center gap-1">
            <MapPin size={12} weight="duotone" /> Location is captured automatically when you submit.
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium text-ink-soft">Cancel</button>
          <button data-testid="complete-submit" disabled={submitting} onClick={submit} className="px-4 py-2 rounded-full bg-sage text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-1">
            <CheckCircle weight="duotone" /> {submitting ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </div>
    </div>
  );
}
